import { Task } from '@/types/task';

export interface TaskUpdateResult {
    updates: Partial<Task>;
    extraUpdates?: Partial<Task>;
    shouldToggle?: boolean;
}

/**
 * Calculates updates for starting a task timer.
 */
export function calculateStartTaskUpdates(task: Task, now: Date = new Date()): Partial<Task> {
    const baseMs = task.actualDurationMinutes ? task.actualDurationMinutes * 60000 : 0;
    // If we have previous duration, we "resume" by setting the start time back
    // so that (now - start) equals the previous duration + new elapsed time.
    const resumedStart = baseMs ? new Date(now.getTime() - baseMs) : now;

    return {
        status: 'started',
        timerStartedAt: resumedStart,
        progress: task.progress || 0,
        completed: false,
        completedBy: null,
        lastCompletedDate: null
    };
}

/**
 * Calculates updates for stopping/pausing a task timer.
 */
export function calculateStopTaskUpdates(task: Task, now: Date = new Date()): Partial<Task> {
    if (!task.timerStartedAt) {
        return { status: 'pending', timerStartedAt: null };
    }

    const startedAt = typeof task.timerStartedAt === 'string'
        ? new Date(task.timerStartedAt)
        : task.timerStartedAt;

    const elapsedMs = now.getTime() - startedAt.getTime();
    const rawMinutes = elapsedMs / 60000;
    // Ensure at least 0.01 mins if it was running, to avoid 0
    const elapsedMinutes = Math.max(0.01, Math.round(rawMinutes * 100) / 100);

    return {
        status: 'pending',
        timerStartedAt: null,
        actualDurationMinutes: elapsedMinutes
    };
}

/**
 * Calculates updates for toggling a task's completion status.
 * If the task is currently running (timer active), it stops the timer and saves the duration
 * as part of the completion process.
 */
export function calculateToggleCompletionUpdates(task: Task, now: Date = new Date()): TaskUpdateResult {
    const updates: Partial<Task> = {};
    let extraUpdates: Partial<Task> | undefined;

    // Case 1: Task is already completed -> Mark as incomplete
    if (task.completed) {
        // We don't need any special calculation for un-completing beyond the toggle itself,
        // unless we want to resume the timer (which we usually don't do automatically on un-complete)
        return { shouldToggle: true, updates };
    }

    // Case 2: Task is incomplete -> Mark as completed

    // If timer is running, stop it and save duration
    if (task.timerStartedAt) {
        const stopUpdates = calculateStopTaskUpdates(task, now);

        // We want to save these updates (duration, etc.) separately or merge them?
        // The UI `onToggle` usually handles the toggle boolean itself, but might accept extra updates.
        // In the existing code: `onToggle(id, completionType, extraUpdates)`

        extraUpdates = {
            ...stopUpdates,
            // Ensure we clear timer state
            timerStartedAt: null
        };
    }

    return {
        shouldToggle: true,
        updates,
        extraUpdates
    };
}

/**
 * Calculates updates for grabbing (assigning to self) or ungrabbing a task.
 */
export function calculateGrabTaskUpdates(task: Task, userId: string): TaskUpdateResult {
    const assignedTo = task.assignedTo || [];
    const isAssigned = assignedTo.includes(userId);

    const nextAssignedTo = isAssigned
        ? assignedTo.filter(id => id !== userId)
        : [...assignedTo, userId];

    return {
        updates: {
            assignedTo: nextAssignedTo
        }
    };
}

/**
 * Calculates the median duration from a list of tasks.
 */
export function calculateMedianDuration(tasks: Task[]): number | null {
    if (!tasks || tasks.length === 0) return null;

    const durations = tasks
        .map(t => t.actualDurationMinutes)
        .filter((d): d is number => d !== null && d !== undefined && d > 0)
        .sort((a, b) => a - b);

    if (durations.length === 0) return null;

    const mid = Math.floor(durations.length / 2);

    if (durations.length % 2 !== 0) {
        return durations[mid];
    } else {
        return (durations[mid - 1] + durations[mid]) / 2;
    }
}
